import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HonorificTitle } from '../../../models/honorificTitle';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-honorific-title-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './honorific-title-list.component.html',
  styleUrl: './honorific-title-list.component.scss'
})
export class HonorificTitleListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  honorificTitles = signal<HonorificTitle[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadHonorificTitles();
  }

  loadHonorificTitles(): void {
    this.spinnerService.show();
    this.lutService.getHonorificTitles().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortHonorificTitles(data);
        this.honorificTitles.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading honorific titles:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortHonorificTitles(honorificTitles: HonorificTitle[]): HonorificTitle[] {
    return [...honorificTitles].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateHonorificTitle(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(honorificTitle: HonorificTitle): void {
  }

  onEditHonorificTitle(honorificTitle: HonorificTitle): void {
  }

  onDeleteHonorificTitle(honorificTitle: HonorificTitle): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(honorificTitle.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalHonorificTitles = this.honorificTitles();

          // 1. Optimistically remove from local state immediately
          this.honorificTitles.update(honorificTitles => honorificTitles.filter(ht => ht.id !== honorificTitle.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteHonorificTitle(honorificTitle.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Honorific title deleted successfully');
              // Update cache so other components get updated data
              this.lookupDataService.updateTableData('honorificTitles', this.honorificTitles());
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting honorific title:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.honorificTitles.set(originalHonorificTitles);
              this.toast.error(`<strong>Failed to delete Honorific Title:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: HonorificTitle, newName: string} | {item: HonorificTitle, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: HonorificTitle;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.honorificTitles.update(honorificTitles => {
      const updated = honorificTitles.map(ht => ht.id === event.item.id ? updatedItem : ht);
      return this.sortHonorificTitles(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateHonorificTitle(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Honorific title updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('honorificTitles', this.honorificTitles());
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.honorificTitles.update(honorificTitles => {
          const updated = honorificTitles.map(ht => ht.id === event.item.id ? response : ht);
          return this.sortHonorificTitles(updated);
        });
      },
      error: (error) => {
        console.error('Error updating honorific title:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.honorificTitles.update(honorificTitles =>
          honorificTitles.map(ht => ht.id === event.item.id ? originalItem : ht)
        );
        this.toast.error(`<strong>Failed to update Honorific Title:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newHonorificTitle: HonorificTitle = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.honorificTitles.update(honorificTitles => this.sortHonorificTitles([...honorificTitles, newHonorificTitle]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createHonorificTitle({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Honorific title created successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('honorificTitles', this.honorificTitles());
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.honorificTitles.update(honorificTitles => {
          const updated = honorificTitles.map(ht => ht.id === tempId ? response : ht);
          return this.sortHonorificTitles(updated);
        });
      },
      error: (error) => {
        console.error('Error creating honorific title:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.honorificTitles.update(honorificTitles =>
          honorificTitles.filter(ht => ht.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Honorific Title:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
